import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '@env'
import { MetricMap } from '@core/models/metric.models'
import { Observable } from 'rxjs'
import { Player, PlayerComparisonRequest, PlayerRequest } from '@core/models/player.models'
import { PlayerPosition } from '@core/enums/player.enums'
import { Aggregation } from '@features/aggregations/models/aggregations.models'

@Injectable({ providedIn: 'root' })
export class PlayerApi {
  private readonly _http: HttpClient = inject(HttpClient)

  fetchPlayer({ playerId, seasonId, competitionId, teamId }: PlayerRequest): Observable<Player> {
    return this._http.get<Player>(environment.api.playersUrlFn(seasonId, competitionId, teamId, playerId))
  }

  fetchMatchPlayer(
    seasonId: string,
    competitionId: string,
    matchId: string,
    playerId: string,
    teamId: string,
    leftAggregationDataSource: Aggregation,
    aggregationDataSource: Aggregation,
    playerPosition: PlayerPosition
  ) {
    const params = playerPosition
      ? { leftAggregationDataSource, aggregationDataSource, playerPosition }
      : { leftAggregationDataSource, aggregationDataSource }

    return this._http.get<Player>(
      environment.api.matchPlayerWithContextUrlFn(seasonId, competitionId, matchId, teamId, playerId),
      { params }
    )
  }

  compare(comparedPlayersData: PlayerComparisonRequest) {
    return this._http.post<any>(environment.api.comparePlayersUrl, comparedPlayersData)
  }

  compareInMatch(matchId: string, comparedPlayersData: PlayerComparisonRequest): Observable<any> {
    return this._http.post<any>(environment.api.matchComparePlayersUrlFn(matchId), comparedPlayersData)
  }

  fetchPlayerMetrics(
    seasonId: string,
    competitionId: string,
    teamId: string,
    id: string,
    playerPosition: PlayerPosition,
    aggregationDataSource: Aggregation,
    leftAggregationDatasource: Aggregation
  ): Observable<MetricMap> {
    let params: Record<string, PlayerPosition | Aggregation> = { aggregationDataSource, leftAggregationDatasource }

    if (playerPosition) {
      params = { ...params, playerPosition }
    }

    return this._http.get<MetricMap>(environment.api.playerMetricResultsUrlFn(seasonId, competitionId, teamId, id), {
      params
    })
  }

  searchPlayer(searchParams: string, competitionId: string, seasonId: string): Observable<Player[]> {
    return this._http.get<Player[]>(environment.api.playersSearchUrl, {
      params: { value: searchParams, competitionId, seasonId }
    })
  }
}
